import React from 'react';

import { cssMap as unbouncedCssMap } from '@compiled/react';

import Avatar from '@atlaskit/avatar';
import { cssMap } from '@atlaskit/css';
import { fg } from '@atlaskit/platform-feature-flags';
import { Box, Stack, Text } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';
import { WidthObserver } from '@atlaskit/width-detector';

import { type DirectoryPerson } from '../../../../types';

import { PersonCardWrapper } from './person-card-wrapper';

const AVATAR_BREAKPOINT = 180;

type PersonCardProps = {
	data: DirectoryPerson;
};

const unbouncedStyles = unbouncedCssMap({
	/**
	 * This is a workaround to fix the issue where the name is not properly truncated
	 * when it is too long as we are using Box to add editional styling around font size and weight.
	 */
	name: {
		font: token('font.heading.xsmall'),
		fontWeight: token('font.weight.medium'),
		textAlign: 'center',
		overflow: 'hidden',
		textOverflow: 'ellipsis',
		display: '-webkit-box',
		WebkitBoxOrient: 'vertical',
		WebkitLineClamp: '2',
	},
});

const styles = cssMap({
	emptyLine: {
		height: '20px',
	},
});

export function PersonCard({
	data: { avatarUrl, id, name, jobTitle, location, localTime },
}: PersonCardProps) {
	const shouldShowEmptyLine = !jobTitle && !location && !localTime;
	const [width, setWidth] = React.useState(0);

	return (
		<PersonCardWrapper id={id}>
			<WidthObserver setWidth={setWidth} />
			<Stack space="space.150" alignInline="center">
				<Avatar
					src={avatarUrl}
					name={name}
					size={width < AVATAR_BREAKPOINT ? 'xlarge' : 'xxlarge'}
				/>
				<Stack space="space.050" alignInline="center">
					{fg('enable_a11y_fix_location_directory_1') ? (
						<Box as="h2" xcss={unbouncedStyles.name}>
							{name}
						</Box>
					) : (
						<Text maxLines={2} size="medium" weight="medium" align="center">
							{name}
						</Text>
					)}
					<Text maxLines={2} size="small" color="color.text.subtlest" align="center">
						{jobTitle}
					</Text>
					<Text maxLines={1} size="small" color="color.text.subtlest" align="center">
						{[location, localTime].filter(Boolean).join(' ')}
					</Text>

					{/* Show a single empty line under the name if no extra fields are populated.
						Note - cards will still take the same size as the tallest in the row. */}
					{shouldShowEmptyLine && <Box xcss={styles.emptyLine} />}
				</Stack>
			</Stack>
		</PersonCardWrapper>
	);
}
